import { PagePaginationFromKeys, ServerResponse } from '../../../../types'
import { AccountPricingModelEnum } from '../../../../user'
import { UsersUser, UsersUserAccount } from '../../Users/api/types'

export type CommissionBenchmarkAccount = {
  accountId: number
  userId: number
  login: string | null
  server: string | null
}

export type CommissionBenchmarkSymbolStats = {
  symbol: string
  totalTrades: number
  totalLots: number
  totalCommission: number
  avgCommissionPerLot: number
  avgCommissionPerTrade: number
  totalSwapNet: number
  totalSwapPaid: number
  totalSwapReceived: number
  swapTradeCount: number
}

export type SpreadBenchmarkSymbolStats = {
  symbol: string
  medianSpreadPoints: number
  avgSpreadPoints: number
  medianSpreadUsdPerLot: number
  avgSpreadUsdPerLot: number
  sampleCount: number
}

export type CommissionBenchmarkReferenceResponse = ServerResponse<{
  account: CommissionBenchmarkAccount
  stats: Record<string, CommissionBenchmarkSymbolStats>
  spreadStats: Record<string, SpreadBenchmarkSymbolStats>
  monthKey: string
}>

export type CommissionBenchmarkAccountOverviewItem = {
  account: UsersUserAccount
  user: UsersUser
  totalSymbols: number
  symbolsWorseThanNuvesto: number
  maxCommissionDeviation: number | null
  totalCommissionPaid: number
  totalNuvestoCommissionPaid: number
  totalCommissionDifference: number
  totalSwapNet: number
  totalSwapPaid: number
  totalSwapReceived: number
  swapTradeCount: number
  totalSpreadDifferenceUsd: number
  totalCombinedDifference: number
  totalSymbolsWithSpread: number
  symbolsWorseThanNuvestoBySpread: number
  maxSpreadDeviationPoints: number | null
  avgSpreadDifferencePoints: number
  accountMedianSpreadPoints: number
  nuvestoMedianSpreadPoints: number
  avgSpreadUsdDifferencePerLot: number
}

export enum CommissionBenchmarkOverviewColumnsEnum {
  totalSymbols = 'totalSymbols',
  symbolsWorseThanNuvesto = 'symbolsWorseThanNuvesto',
  maxCommissionDeviation = 'maxCommissionDeviation',
  totalCommissionPaid = 'totalCommissionPaid',
  totalNuvestoCommissionPaid = 'totalNuvestoCommissionPaid',
  totalCommissionDifference = 'totalCommissionDifference',
  totalSwapNet = 'totalSwapNet',
  totalSpreadDifferenceUsd = 'totalSpreadDifferenceUsd',
  totalCombinedDifference = 'totalCombinedDifference',
}

export type CommissionBenchmarkOverviewColumnsKey = CommissionBenchmarkOverviewColumnsEnum

export type CommissionBenchmarkOverviewColumnsType = Record<
  | CommissionBenchmarkOverviewColumnsEnum
  | 'userName'
  | 'country'
  | 'email'
  | 'phone'
  | 'accountServer'
  | 'accountBalance'
  | 'isDemo'
  | 'isCfd'
  | 'isProp'
  | 'platform'
  | 'pricingModel'
  | 'accountMedianSpreadPoints'
  | 'nuvestoMedianSpreadPoints'
  | 'avgSpreadDifferencePoints'
  | 'avgSpreadUsdDifferencePerLot'
  | 'totalSymbolsWithSpread'
  | 'symbolsWorseThanNuvestoBySpread'
  | 'maxSpreadDeviationPoints'
  | 'totalSwapNet'
  | 'totalSwapPaid'
  | 'totalSwapReceived'
  | 'swapTradeCount'
  | 'totalSpreadDifferenceUsd'
  | 'totalCombinedDifference',
  string
> & { empty: string; actions: string }

export type CommissionBenchmarkOverviewPagination =
  PagePaginationFromKeys<CommissionBenchmarkOverviewColumnsKey>

export type CommissionBenchmarkAccountOverviewResponse = ServerResponse<{
  benchmarkAccountId: number
  accounts: CommissionBenchmarkAccountOverviewItem[]
  meta: CommissionBenchmarkOverviewPagination
  monthKey: string
}>

export enum CommissionBenchmarkNuvestoAccountTypeEnum {
  standart = 'STANDART',
  prime = 'PRIME',
  vip = 'VIP',
}

export enum CommissionBenchmarkBenchmarkTypeEnum {
  nuvesto = 'NUVESTO',
  traderscale = 'TRADERSCALE',
}

export type CommissionBenchmarkReferenceRequest = {
  benchmarkType?: CommissionBenchmarkBenchmarkTypeEnum
  nuvestoAccountType?: CommissionBenchmarkNuvestoAccountTypeEnum
}

export type GetCommissionBenchmarkOverviewRequest = Pick<
  CommissionBenchmarkOverviewPagination,
  'page' | 'perPage' | 'order' | 'orderBy'
> & {
  showDemo?: boolean
  showProp?: boolean
  showCfd?: boolean
  onlyWorseThanNuvesto?: boolean
  pricingModel?: AccountPricingModelEnum
  benchmarkType?: CommissionBenchmarkBenchmarkTypeEnum
  nuvestoAccountType?: CommissionBenchmarkNuvestoAccountTypeEnum
  search?: string
}

export type CommissionBenchmarkAccountBreakdownItem = {
  symbol: string
  accountAvgCommissionPerLot: number
  nuvestoAvgCommissionPerLot: number
  difference: number
  totalLots: number
  totalTrades: number
  totalSwapNet: number
  totalSwapPaid: number
  totalSwapReceived: number
  swapTradeCount: number
  accountMedianSpreadPoints: number
  nuvestoMedianSpreadPoints: number
  spreadDifferencePoints: number
  accountMedianSpreadUsdPerLot: number
  nuvestoMedianSpreadUsdPerLot: number
  spreadDifferenceUsdPerLot: number
  spreadSampleCount: number
}

export type CommissionBenchmarkAccountBreakdownResponse = ServerResponse<{
  benchmarkAccountId: number
  account: CommissionBenchmarkAccount
  symbols: CommissionBenchmarkAccountBreakdownItem[]
  monthKey: string
}>

export type CommissionBenchmarkRecalculateAccountSpreadResponse = ServerResponse<{
  accountId: number
  updatedSymbols: number
}>
